import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Volume2, Star, HelpCircle, RotateCcw, Send } from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const FillInTheBlank = ({ questions, onComplete, childId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [completed, setCompleted] = useState(false);

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex) / questions.length) * 100;

  const speakText = (text) => {
    _speakTTS((text || '').replace('___', 'blank'), 0.85);
  };

  const normalizeAnswer = (answer) => {
    return answer.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (showFeedback || !userAnswer.trim()) return;
    
    setShowFeedback(true);
    
    // Check answer (case-insensitive, ignore punctuation)
    const correct = normalizeAnswer(userAnswer) === normalizeAnswer(currentQuestion.answer);
    setIsCorrect(correct);
    
    if (correct) {
      setScore(s => s + 1);
      toast.success('Correct! Well done!', { duration: 1500 });
    } else {
      toast.error(`The answer is: ${currentQuestion.answer}`, { duration: 2500 });
    }

    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(i => i + 1);
        setUserAnswer('');
        setShowFeedback(false);
        setIsCorrect(false);
        setShowHint(false);
      } else {
        setCompleted(true);
        const finalScore = Math.round(((score + (correct ? 1 : 0)) / questions.length) * 100);
        if (onComplete) {
          onComplete(finalScore, questions.length);
        }
      }
    }, 2000);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setScore(0);
    setUserAnswer('');
    setShowFeedback(false);
    setIsCorrect(false);
    setShowHint(false);
    setCompleted(false);
  };

  // Render sentence with blank
  const renderSentence = () => {
    const parts = currentQuestion.sentence.split('___');
    return (
      <span className="text-xl leading-relaxed">
        {parts[0]}
        <span className={`inline-block min-w-20 mx-2 px-3 py-1 rounded-lg border-2 border-dashed ${
          showFeedback 
            ? isCorrect 
              ? 'border-green-500 bg-green-100 text-green-800' 
              : 'border-red-500 bg-red-100 text-red-800'
            : 'border-purple-400 bg-purple-50'
        }`}>
          {showFeedback ? currentQuestion.answer : userAnswer || '?'}
        </span>
        {parts[1]}
      </span>
    );
  };

  if (completed) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center">
          <Star className="w-12 h-12 text-white" />
        </div>
        <h2 className="font-fredoka text-3xl font-bold text-emerald-800 mb-2">Great Job!</h2>
        <p className="text-xl text-emerald-600 mb-4">You scored {score} out of {questions.length}</p>
        <div className="w-48 mx-auto mb-6">
          <Progress value={percentage} className="h-4" />
          <p className="text-sm text-slate-500 mt-1">{percentage}%</p>
        </div>
        <Button onClick={handleReset} variant="outline" className="gap-2">
          <RotateCcw className="w-4 h-4" /> Try Again
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fill-in-blank-game">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-slate-500">
          <span>Question {currentIndex + 1} of {questions.length}</span>
          <span>{score} correct</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-6">
                <h3 className="font-fredoka text-lg font-bold text-green-800">
                  Fill in the blank:
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => speakText(currentQuestion.sentence)}
                    className="text-green-600"
                  >
                    <Volume2 className="w-5 h-5" />
                  </Button>
                  {currentQuestion.hint && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowHint(!showHint)}
                      className="text-amber-600"
                    >
                      <HelpCircle className="w-5 h-5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Sentence with blank */}
              <div className="text-center mb-6 p-4 bg-white rounded-xl">
                {renderSentence()}
              </div>

              {/* Hint */}
              {showHint && currentQuestion.hint && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4"
                >
                  <p className="text-amber-800 text-sm">💡 Hint: {currentQuestion.hint}</p>
                </motion.div>
              )}

              {/* Word bank if provided */}
              {currentQuestion.wordBank && (
                <div className="mb-4">
                  <p className="text-sm text-slate-500 mb-2">Word Bank:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentQuestion.wordBank.map((word) => (
                      <Button
                        key={word}
                        variant="outline"
                        size="sm"
                        onClick={() => setUserAnswer(word)}
                        disabled={showFeedback}
                        className="text-green-700 border-green-300 hover:bg-green-50"
                      >
                        {word}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input form */}
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Type your answer..."
                  disabled={showFeedback}
                  className="flex-1 text-lg"
                  autoFocus
                  data-testid="fill-blank-input"
                />
                <Button 
                  type="submit" 
                  disabled={showFeedback || !userAnswer.trim()}
                  className="bg-green-500 hover:bg-green-600"
                  data-testid="fill-blank-submit"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </form>

              {/* Feedback */}
              {showFeedback && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                    isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {isCorrect ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Perfect!</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5" />
                      <span>The correct answer is: <strong>{currentQuestion.answer}</strong></span>
                    </>
                  )}
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default FillInTheBlank;
